// Reveal-style fade plugin for Embla Carousel v8.
//
// Unlike a crossfade (both slides at partial opacity simultaneously), this keeps
// the incoming slide at full opacity underneath and fades only the outgoing slide
// from 1→0 on top. Eliminates grey "bleed-through" from light backgrounds that
// makes dark slides look washed out at the midpoint.

function clampNumber(number, min, max) {
  return Math.min(Math.max(number, min), max)
}

function isNumber(value) {
  return typeof value === 'number' && !isNaN(value)
}

function FadeReveal(userOptions = {}) {
  const fullOpacity = 1
  const noOpacity = 0
  const fadeFriction = 0.68

  let emblaApi
  let opacities = []
  let fadeToNextDistance
  let distanceFromPointerDown = 0
  let fadeVelocity = 0
  let progress = 0

  let defaultSettledBehaviour
  let defaultProgressBehaviour

  function init(emblaApiInstance) {
    emblaApi = emblaApiInstance

    const selectedSnap = emblaApi.selectedScrollSnap()
    const { scrollBody, containerRect, axis } = emblaApi.internalEngine()
    const containerSize = axis.measureSize(containerRect)

    fadeToNextDistance = clampNumber(containerSize * 0.75, 200, 500)

    opacities = emblaApi
      .scrollSnapList()
      .map((_, index) => (index === selectedSnap ? fullOpacity : noOpacity))

    defaultSettledBehaviour = scrollBody.settled
    defaultProgressBehaviour = emblaApi.scrollProgress

    scrollBody.settled = fadeSettled
    emblaApi.scrollProgress = fadeScrollProgress

    emblaApi
      .on('select', select)
      .on('slideFocus', fadeToSelectedSnapInstantly)
      .on('pointerDown', pointerDown)
      .on('pointerUp', pointerUp)

    disableScroll()
    fadeToSelectedSnapInstantly()
  }

  function destroy() {
    const { scrollBody } = emblaApi.internalEngine()
    scrollBody.settled = defaultSettledBehaviour
    emblaApi.scrollProgress = defaultProgressBehaviour

    emblaApi
      .off('select', select)
      .off('slideFocus', fadeToSelectedSnapInstantly)
      .off('pointerDown', pointerDown)
      .off('pointerUp', pointerUp)

    emblaApi.slideNodes().forEach((slideNode) => {
      const style = slideNode.style
      style.opacity = ''
      style.transform = ''
      style.pointerEvents = ''
      style.zIndex = ''
      if (!slideNode.getAttribute('style')) slideNode.removeAttribute('style')
    })
  }

  function fadeScrollProgress() {
    return progress
  }

  function pointerDown() {
    distanceFromPointerDown = 0
    fadeVelocity = 0
  }

  function pointerUp() {}

  function select() {
    const duration = emblaApi.internalEngine().scrollBody.duration()
    fadeVelocity = duration ? 0 : fullOpacity
    if (!duration) fadeToSelectedSnapInstantly()
  }

  function fadeToSelectedSnapInstantly() {
    const selectedSnap = emblaApi.selectedScrollSnap()
    emblaApi.scrollSnapList().forEach((_, index) => {
      opacities[index] = index === selectedSnap ? fullOpacity : noOpacity
    })
    applyAllOpacities(selectedSnap)
  }

  function getFadeIndex() {
    const { dragHandler, index, scrollBody } = emblaApi.internalEngine()
    const selectedSnap = emblaApi.selectedScrollSnap()

    if (!dragHandler.pointerDown()) return selectedSnap

    const directionSign = Math.sign(scrollBody.velocity())
    const distanceSign = Math.sign(distanceFromPointerDown)
    const nextSnap = index.clone().set(selectedSnap).add(directionSign * -1).get()

    if (!directionSign || !distanceSign) return null
    return distanceSign === directionSign ? nextSnap : selectedSnap
  }

  // Returns the highest-opacity non-incoming slide index (the slide fading out).
  function getOutgoingIndex(incomingIndex) {
    let maxOpacity = -1
    let outgoing = null
    emblaApi.scrollSnapList().forEach((_, i) => {
      if (i !== incomingIndex && opacities[i] > maxOpacity) {
        maxOpacity = opacities[i]
        outgoing = i
      }
    })
    return maxOpacity > 0 ? outgoing : null
  }

  function getSlideTransform(position) {
    const { axis } = emblaApi.internalEngine()
    const translateAxis = axis.scroll.toUpperCase()
    return `translate${translateAxis}(${axis.direction(position)}px)`
  }

  function disableScroll() {
    const { translate, slideLooper } = emblaApi.internalEngine()
    translate.clear()
    translate.toggleActive(false)
    slideLooper.loopPoints.forEach(({ translate: t }) => {
      t.clear()
      t.toggleActive(false)
    })
    emblaApi.containerNode().style.transform = 'translate(0px,0px)'
  }

  function lockExcessiveScroll(incomingIndex) {
    const { scrollSnaps, location, target } = emblaApi.internalEngine()
    if (!isNumber(incomingIndex)) return

    // Lock once the outgoing has faded past halfway (mirrors original's "incoming > 0.5").
    const maxOutgoingOpacity = emblaApi
      .scrollSnapList()
      .reduce((max, _, i) => (i !== incomingIndex ? Math.max(max, opacities[i]) : max), 0)

    if (maxOutgoingOpacity > 0.5) return
    location.set(scrollSnaps[incomingIndex])
    target.set(location)
  }

  function setSlideOpacity(index, incomingIndex, outgoingIndex) {
    const slidesInSnap = emblaApi.internalEngine().slideRegistry[index]
    const { scrollSnaps, containerRect } = emblaApi.internalEngine()
    const opacity = opacities[index]

    slidesInSnap.forEach((slideIndex) => {
      const style = emblaApi.slideNodes()[slideIndex].style
      const roundedOpacity = parseFloat(opacity.toFixed(2))
      const hasOpacity = roundedOpacity > noOpacity
      const position = hasOpacity ? scrollSnaps[index] : containerRect.width + 2
      const transform = getSlideTransform(position)

      if (hasOpacity) style.transform = transform
      style.opacity = roundedOpacity.toString()
      style.pointerEvents = opacity > 0.5 ? 'auto' : 'none'
      // Outgoing sits on top so it fades over the fully-visible incoming slide below.
      style.zIndex = index === outgoingIndex && hasOpacity ? '2' : '1'
      if (!hasOpacity) style.transform = transform
    })
  }

  function applyAllOpacities(incomingIndex) {
    const outgoingIndex = getOutgoingIndex(incomingIndex)
    emblaApi.scrollSnapList().forEach((_, index) => {
      setSlideOpacity(index, incomingIndex, outgoingIndex)
    })
  }

  function setProgress(incomingIndex) {
    const { index, dragHandler, scrollSnaps } = emblaApi.internalEngine()
    const pointerIsDown = dragHandler.pointerDown()
    const snapFraction = 1 / (scrollSnaps.length - 1)

    let indexA = incomingIndex
    let indexB = pointerIsDown
      ? emblaApi.selectedScrollSnap()
      : emblaApi.previousScrollSnap()

    if (pointerIsDown && indexA === indexB) {
      const reverseSign = Math.sign(distanceFromPointerDown) * -1
      indexA = indexB
      indexB = index.clone().set(indexB).add(reverseSign).get()
    }

    const outgoing = getOutgoingIndex(incomingIndex)
    const outgoingOpacity = outgoing != null ? opacities[outgoing] : 0
    const currentPosition = indexB * snapFraction
    const diffPosition = (indexA - indexB) * snapFraction
    progress = currentPosition + diffPosition * (1 - outgoingOpacity)
  }

  function setOpacities(incomingIndex, velocity) {
    const absVelocity = Math.abs(velocity)

    emblaApi.scrollSnapList().forEach((_, index) => {
      if (index === incomingIndex) {
        opacities[index] = fullOpacity
      } else {
        opacities[index] = Math.max(opacities[index] - absVelocity, noOpacity)
      }
    })

    setProgress(incomingIndex)
    applyAllOpacities(incomingIndex)
  }

  function fade(emblaApiRef) {
    const { dragHandler, scrollBody } = emblaApiRef.internalEngine()
    const pointerIsDown = dragHandler.pointerDown()
    const velocity = scrollBody.velocity()
    const duration = scrollBody.duration()
    const incomingIndex = getFadeIndex()
    const noIncomingIndex = !isNumber(incomingIndex)

    if (pointerIsDown) {
      if (!velocity) return
      distanceFromPointerDown += velocity
      fadeVelocity = Math.abs(velocity / fadeToNextDistance)
      lockExcessiveScroll(incomingIndex)
    }

    if (!pointerIsDown) {
      if (!duration || noIncomingIndex) return
      // Converge outgoing toward 0 — mirrors original's "converge incoming toward 1".
      const outgoing = getOutgoingIndex(incomingIndex)
      const outgoingOpacity = outgoing != null ? opacities[outgoing] : 0
      fadeVelocity += outgoingOpacity / duration
      fadeVelocity *= fadeFriction
    }

    if (noIncomingIndex) return
    setOpacities(incomingIndex, fadeVelocity)
  }

  function fadeSettled() {
    const { target, location } = emblaApi.internalEngine()
    const diffToTarget = target.get() - location.get()
    const notReachedTarget = Math.abs(diffToTarget) >= 1
    const incomingIndex = getFadeIndex()
    const noIncomingIndex = !isNumber(incomingIndex)

    fade(emblaApi)

    if (noIncomingIndex || notReachedTarget) return false

    // Settled once every non-incoming slide has fully faded out.
    return emblaApi.scrollSnapList().every((_, i) => i === incomingIndex || opacities[i] < 0.001)
  }

  const self = {
    name: 'fadeReveal',
    options: userOptions,
    init,
    destroy,
  }
  return self
}

FadeReveal.globalOptions = undefined

export default FadeReveal
